import axios, { AxiosError } from 'axios';

// Define base URL - Firebase Functions URL (Cloud Run)
const API_BASE_URL = 'https://api-6ul3istoeq-uc.a.run.app';

// API Key - hardcoded key from our server
const API_KEY = 'sk_5eb6ee5964f75c2c29e2a61000cca8a08dda51f266f36f97';

// Configure axios instance with default headers
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY  // Changed to lowercase to match server expectations
  }
});

// Type for access pass generation request
export interface GenerateAccessPassRequest {
  customerName: string;
  contactNumber: string;
  validDate: string;  // Format: YYYY-MM-DD
  pickupTime: string; // Format: HH:MM (24-hour)
  returnTime: string; // Format: HH:MM (24-hour)
  type: string;       // Collection, Return, etc.
  qrid?: string;      // Optional QRID if available
}

// Type for the server response
export interface ServerResponse {
  success: boolean;
  message: string;
  uniqueId?: string;
  sheetResponse?: {
    updatedRange: string;
    updatedRows: number;
    updatedColumns: number;
    updatedCells: number;
  };
  error?: string;
}

/**
 * Generate an access pass by submitting data to the Firebase Function
 * This sends the data to Google Sheets via the server
 * @param data Access pass details
 * @returns Promise with server response
 */
export const generateAccessPass = async (data: GenerateAccessPassRequest): Promise<ServerResponse> => {
  try {
    console.log('Sending access pass generation request:', data);
    
    const response = await apiClient.post<ServerResponse>('/api/submit', data);
    
    console.log('Server response:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error generating access pass:', error);
    
    // Format error response
    if (axios.isAxiosError(error)) {
      // Server returned an error response
      return {
        success: false,
        message: 'Server error',
        error: error.response?.data?.error || error.message
      };
    } else {
      // Network error or other issue
      return {
        success: false,
        message: 'Failed to connect to server',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
};

/**
 * Check if the server is running
 * @returns Promise with health check result
 */
export const checkServerHealth = async (): Promise<{ status: string; message: string }> => {
  try {
    const response = await apiClient.get('/api/health');
    return response.data;
  } catch (error: unknown) {
    console.error('Server health check failed:', error);
    return {
      status: 'error',
      message: 'Server is unreachable'
    };
  }
};

/**
 * Test the Google Sheets authentication
 * @returns Promise with auth test result
 */
export const testGoogleSheetsAuth = async (): Promise<{ success: boolean; message: string; spreadsheetTitle?: string; error?: string }> => {
  try {
    const response = await apiClient.get('/api/test-auth');
    return response.data;
  } catch (error: unknown) {
    console.error('Google Sheets auth test failed:', error);
    return {
      success: false,
      message: 'Authentication test failed',
      error: axios.isAxiosError(error) ? error.response?.data?.error || error.message : 'Unknown error'
    };
  }
};

/**
 * Get server version and status
 * @returns Promise with server info
 */
export const getServerInfo = async (): Promise<{ status: string; version: string }> => {
  try {
    const response = await apiClient.get('/api/health');
    return {
      status: response.data.status,
      version: response.data.version || 'unknown'
    };
  } catch (error: unknown) {
    console.error('Failed to get server info:', error);
    return {
      status: 'error',
      version: 'unknown'
    };
  }
}; 